import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Calendar, AlertTriangle, Clock, Loader2, MoreVertical,
  Pencil, CheckCircle2, Trash2,
} from "lucide-react";
import { useDueAlerts, type DueAlert } from "@/hooks/use-due-alerts";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";

interface DueBillsWidgetProps {
  maxItems?: number;
  onRefresh?: () => void;
  onViewTransaction?: (alert: DueAlert) => void;
}

export const DueBillsWidget = ({ maxItems = 5, onRefresh, onViewTransaction }: DueBillsWidgetProps) => {
  const { alerts, loading, markAsPaid, refetch } = useDueAlerts();
  const [deleteAlert, setDeleteAlert] = useState<DueAlert | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleMarkAsPaid = async (id: string) => {
    const success = await markAsPaid(id);
    if (success) {
      toast({ title: "Conta marcada como paga!" });
      onRefresh?.();
    }
  };

  const handleDelete = async () => {
    if (!deleteAlert) return;
    try {
      const { error } = await supabase
        .from("transactions")
        .delete()
        .eq("id", deleteAlert.id);
      if (error) throw error;
      toast({ title: "Lançamento excluído" });
      setDeleteAlert(null);
      refetch();
      onRefresh?.();
    } catch (error: any) {
      toast({ variant: "destructive", title: "Erro ao excluir", description: error.message });
    }
  };

  // Combine all alerts and take first N
  const allAlerts = [
    ...alerts.overdue,
    ...alerts.dueToday,
    ...alerts.dueSoon,
    ...alerts.upcoming,
  ].slice(0, maxItems);

  const hasOverdue = alerts.overdue.length > 0;
  const hasDueToday = alerts.dueToday.length > 0;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Próximas Contas
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      hasOverdue && "border-destructive/50",
      !hasOverdue && hasDueToday && "border-warning/50"
    )}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Próximas Contas
          </div>
          {hasOverdue && (
            <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded-full flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {alerts.overdue.length} atrasada{alerts.overdue.length > 1 ? "s" : ""}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {allAlerts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhuma conta pendente nos próximos dias
          </p>
        ) : (
          <>
            {allAlerts.map((alert) => (
              <div
                key={alert.id}
                className={cn(
                  "flex items-center justify-between p-2 rounded-lg transition-colors",
                  alert.urgency === "overdue" && "bg-destructive/10",
                  alert.urgency === "today" && "bg-warning/10",
                  alert.urgency === "soon" && "bg-muted/50",
                  alert.urgency === "upcoming" && "bg-muted/30"
                )}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{alert.description}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {alert.urgency === "overdue" && (
                      <span className="text-destructive font-medium">
                        {Math.abs(alert.daysUntilDue)} dia{Math.abs(alert.daysUntilDue) > 1 ? "s" : ""} atrasado
                      </span>
                    )}
                    {alert.urgency === "today" && (
                      <span className="text-warning font-medium">Vence hoje</span>
                    )}
                    {alert.urgency === "soon" && (
                      <span>Vence em {alert.daysUntilDue} dia{alert.daysUntilDue > 1 ? "s" : ""}</span>
                    )}
                    {alert.urgency === "upcoming" && (
                      <span>{format(new Date(alert.transaction_date), "dd MMM", { locale: ptBR })}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-destructive">
                    {formatCurrency(alert.amount)}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewTransaction?.(alert)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleMarkAsPaid(alert.id)}>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Marcar como pago
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeleteAlert(alert)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}

            {(alerts.overdue.length + alerts.dueToday.length + alerts.dueSoon.length + alerts.upcoming.length) > maxItems && (
              <p className="text-xs text-center text-muted-foreground pt-2">
                + mais {(alerts.overdue.length + alerts.dueToday.length + alerts.dueSoon.length + alerts.upcoming.length) - maxItems} contas pendentes
              </p>
            )}
          </>
        )}
      </CardContent>

      <AlertDialog open={!!deleteAlert} onOpenChange={(open) => !open && setDeleteAlert(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Lançamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>"{deleteAlert?.description}"</strong> ({deleteAlert ? formatCurrency(deleteAlert.amount) : ""})? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
